/**
 * Flattened submission metrics for dashboards/exports.
 *
 * **SSOT order:** Completeness and overall consistency math live in `shared/nursingComplianceScoring.ts`
 * (re-exported from `./nursingComplianceScoring`). This module applies `normalizeSubmissionShape` +
 * `includedIndexes` (cross-audit / title exclusions), then delegates row/column scoring to that module.
 *
 * **Server parity:** Cloud Functions prebuild copies `shared/nursingComplianceScoring.ts` into
 * `functions/src/`; keep `deriveSubmissionMetrics` in `firestoreSubmissionEncode.ts` aligned with this file.
 */
import { normalizeSubmissionShape } from '../services/submissionShape';
import { completenessFromUnknownRow, overallConsistencyUnweightedAverage } from './nursingComplianceScoring';

/** Inlined to avoid minifier TDZ when bundled (toFiniteNumber was minified to 'me'). */
function toFiniteNum(val: unknown): number | null {
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (typeof val === 'string') {
    const t = val.trim();
    if (t && Number.isFinite(Number(t))) return Number(t);
  }
  return null;
}

/**
 * Flattened numeric metrics on submission docs (dashboard / exports).
 * @see docs/firestore-database-structure.md — `overall_consistency_pct` added in metricsVersion 2.
 */
export type SubmissionMetrics = Record<string, number> & {
  /** Unweighted mean of per-chart consistency where treatments > 0; omitted when none qualify. */
  overall_consistency_pct?: number;
};

/** Discoverable keys for reporting / export code (subset; see deriveSubmissionMetrics). Also `chart_1_*` … `chart_N_*`, `question_1_*` … `question_9_*`. */
export type NursingMetricsReportingKey =
  | 'monthly_score'
  | 'monthly_total'
  | 'monthly_pct'
  | 'disease_pct'
  | 'charts'
  | 'overall_consistency_pct';

/** Typed metric keys for reporting surfaces (avoid stringly-typed `averages['…']`). */
export const METRICS_KEY_OVERALL_CONSISTENCY_PCT = 'overall_consistency_pct' as const satisfies NursingMetricsReportingKey;
export const METRICS_KEY_CHART1_CONSISTENCY_PCT = 'chart_1_consistency_pct' as const;
/** Pooled completeness across charts (0–100), from summed `monthly_score` / `monthly_total` in aggregates. */
export const METRICS_KEY_MONTHLY_PCT = 'monthly_pct' as const satisfies NursingMetricsReportingKey;
/**
 * Pooled ร้อยละความสมบูรณ์รายโรค (0–100). When set (e.g. manual summary SSOT patch),
 * reporting/KPI surfaces prefer this over recomputing from chart answers.
 */
export const METRICS_KEY_DISEASE_PCT = 'disease_pct' as const satisfies NursingMetricsReportingKey;

/**
 * Build flattened, numeric metrics for chart scoring and top-level numeric fields.
 */
export const deriveSubmissionMetrics = (data: unknown): SubmissionMetrics => {
  const metrics: SubmissionMetrics = {};
  if (!data || typeof data !== 'object') return metrics;

  const { answers, focus, treatments, chartTitles } = normalizeSubmissionShape(
    data as Record<string, unknown>,
  );
  const isCrossAudit = (data as { crossAudit?: boolean }).crossAudit === true;

  const shouldExcludeTitle = (title: string) => {
    if (title.includes('ตรวจไขว้')) return true;
    if (title.includes('แก้ไข')) return true;

    const lowered = title.toLowerCase();
    return (
      lowered.includes('edit') ||
      lowered.includes('audit') ||
      lowered.includes('cross audit')
    );
  };

  const includedIndexes =
    !isCrossAudit && chartTitles.length
      ? answers.flatMap((_, idx) => {
          const title = typeof chartTitles[idx] === 'string' ? chartTitles[idx] : '';
          return title && shouldExcludeTitle(title) ? [] : [idx];
        })
      : answers.map((_, idx) => idx);

  const consistencySamples: Array<number | null> = [];

  // 1) Per-chart metrics from answers matrix
  if (includedIndexes.length) {
    let monthlyScore = 0;
    let monthlyTotal = 0;

    includedIndexes.forEach((rowIndex, idx) => {
      const values = Array.isArray(answers[rowIndex]) ? answers[rowIndex] : [];
      const { score, full: total, pct } = completenessFromUnknownRow(values);

      metrics[`chart_${idx + 1}_score`] = score;
      metrics[`chart_${idx + 1}_total`] = total;
      metrics[`chart_${idx + 1}_pct`] = pct;

      monthlyScore += score;
      monthlyTotal += total;

      // 2) Optional per-chart consistency pct from focus/treatments
      const f = toFiniteNum(focus[rowIndex]);
      const t = toFiniteNum(treatments[rowIndex]);
      if (f !== null && t !== null && t > 0) {
        const cPct = Math.round((f / t) * 10000) / 100;
        metrics[`chart_${idx + 1}_consistency_pct`] = cPct;
        consistencySamples.push(cPct);
      } else {
        consistencySamples.push(null);
      }

      // Keep raw numeric fields if present
      if (f !== null) metrics[`chart_${idx + 1}_focus`] = f;
      if (t !== null) metrics[`chart_${idx + 1}_treatments`] = t;
    });

    // Unweighted mean of per-chart (focus/treatments)*100 only where treatments > 0; omit key if none (not 0).
    const overallConsistency = overallConsistencyUnweightedAverage(consistencySamples);
    if (overallConsistency !== null) {
      metrics.overall_consistency_pct = overallConsistency;
    }

    // 3) Per-question metrics (ข้อ 1-9): aggregate by question index across all charts
    const maxQuestions = 9;
    for (let qIdx = 0; qIdx < maxQuestions; qIdx += 1) {
      const column: unknown[] = [];
      includedIndexes.forEach((rowIndex) => {
        const row = Array.isArray(answers[rowIndex]) ? answers[rowIndex] : [];
        const v = row[qIdx];
        if (v !== undefined && v !== null) column.push(v);
      });
      const { score: qScore, full: qTotal, pct: qPct } = completenessFromUnknownRow(column);
      metrics[`question_${qIdx + 1}_score`] = qScore;
      metrics[`question_${qIdx + 1}_total`] = qTotal;
      metrics[`question_${qIdx + 1}_pct`] = qPct;
    }

    metrics['monthly_score'] = monthlyScore;
    metrics['monthly_total'] = monthlyTotal;
    metrics['monthly_pct'] = monthlyTotal ? Math.round((monthlyScore / monthlyTotal) * 10000) / 100 : 0;
    // Average score per chart (ข้อ) for "คะแนนเฉลี่ย" display - sum / number of charts
    metrics['monthly_avg_score'] =
      includedIndexes.length > 0 ? Math.round((monthlyScore / includedIndexes.length) * 100) / 100 : 0;
    metrics['charts'] = includedIndexes.length;

    // Calculate average score only from "รายโรค" (disease-specific) charts
    if (chartTitles.length > 0) {
      let diseaseScore = 0;
      let diseaseChartCount = 0;

      includedIndexes.forEach((rowIndex) => {
        const title = typeof chartTitles[rowIndex] === 'string' ? chartTitles[rowIndex] : '';
        if (title.includes('รายโรค')) {
          const values = Array.isArray(answers[rowIndex]) ? answers[rowIndex] : [];
          const { score } = completenessFromUnknownRow(values);
          diseaseScore += score;
          diseaseChartCount += 1;
        }
      });

      // Average score per "รายโรค" chart (คะแนนรายโรค) - sum / number of disease charts
      if (diseaseChartCount > 0) {
        metrics['disease_monthly_avg_score'] =
          Math.round((diseaseScore / diseaseChartCount) * 100) / 100;
        metrics['disease_charts'] = diseaseChartCount;
      }
    }
  }

  // 3) Small generic numeric extraction (top-level only, safe size)
  // Adds fields like "totalPatients", etc. if they are numeric-like strings.
  Object.entries(data as Record<string, unknown>).forEach(([k, v]) => {
    if (k === 'answers' || k === 'focus' || k === 'treatments') return;
    const n = toFiniteNum(v);
    if (n !== null) metrics[`field_${k}`] = n;
  });

  return metrics;
};
